import { IconButton } from "@/src/components/buttons";
import { openDrawer } from "@/src/components/dialogs";
import { Appointment, AppointmentType, Patient } from "@/src/data/allTypes";
import { USER } from "@/src/data/globalVariables";
import apiFetch from "@/src/utils/apiFetch";
import {
  getItemId,
  getName,
  getServiceId,
  getUserId,
  uid,
} from "@/src/utils/utils";
import { EventSourceInput } from "@fullcalendar/core";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import FullCalendar from "@fullcalendar/react";
import timeGridPlugin from "@fullcalendar/timegrid";
import { useQuery } from "@tanstack/react-query";
import moment from "moment";
import { useRef, useState } from "react";
import { MdOutlinePrint } from "react-icons/md";
import { openAppointmentActionsDialog } from "../appointments/appointmentActions";
import { openAppointmentFrom } from "../appointments/appointmentForm";
import { PatientsList } from "../patients/patientsPage";
import printCalendarDay from "./printCalendarDay";

export function AppointmentsCalendar({
  patient,
  viewType,
  calendarRef,
  start,
  end,
  searchKey,
  selectedUserId,
  selectedAppointmentTypes,
  onDateSelect,
  onAppointmentChange,
}: {
  patient?: Patient | null | undefined;
  calendarRef: React.RefObject<FullCalendar>;
  viewType: "day" | "week" | "month";
  start: string;
  end: string;
  searchKey: string;
  selectedUserId: string | null | undefined;
  selectedAppointmentTypes: (AppointmentType | null | undefined)[];
  onDateSelect:
    | ((start: string, end: string, doctorId: string) => void)
    | undefined
    | null;
  onAppointmentChange: (appointment: Appointment) => void;
}) {
  const [reload, setReload] = useState(0);
  console.log("selectedAppointmentTypes", selectedAppointmentTypes);

  let { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: [
      "CalendarPageFullCalendar",
      searchKey,
      getUserId(),
      viewType,
      start,
      end,
      selectedUserId,
      JSON.stringify(selectedAppointmentTypes),
      reload,
    ],
    queryFn: () => {
      if (!start || !end) return [];
      console.log("selectedUserId", selectedUserId);
      return apiFetch("appointments", "loadMany", {
        start,
        end,
        serviceId: getServiceId(),
        showAll: false,
        doctorId: selectedUserId,
        appointmentTypesIds: selectedAppointmentTypes?.map(
          (type) => getItemId(type) || ""
        ),
      });
    },
  });

  return (
    <div
      key={uid()}
      style={{ height: "100%", width: "100%" }}
      className={`fade ${isLoading ? "disabled" : ""}`}
    >
      <FullCalendar
        ref={calendarRef}
        height={"100%"}
        dragScroll={true}
        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
        initialView={
          (typeof window != "undefined" && window.innerWidth < 800) ||
          viewType == "day"
            ? "timeGridDay"
            : "timeGridWeek"
        }
        initialDate={start || moment().format("YYYY-MM-DD HH:mm:ss")}
        datesSet={(args) => {
          // onDateSelect?.(
          //   moment(args.start).format("YYYY-MM-DD 00:00:00"),
          //   moment(args.end).format("YYYY-MM-DD 23:59:59")
          // );
          return args;
        }}
        events={
          data
            ?.filter((appointment) => {
              if (appointment.status == "refused") return false;
              if (!appointment.start) return false;
              if (getName(appointment.patient)?.trim() == "ereur") return false;
              return true;
            })
            .map((appointment) => {
              if (parseInt(moment(appointment.start).format("HH")) < 8)
                appointment.start = moment(appointment.start).format(
                  "YYYY-MM-DD 08:00:00"
                );
                
              if (parseInt(moment(appointment.start).format("HH")) > 17)
                appointment.start = moment(appointment.start).format(
                  "YYYY-MM-DD 17:30:00"
                );

              return {
                id: appointment.id,
                title: getName(appointment.patient) || "Sans nom",
                start: appointment.start,
                end: appointment.end,
              };
            }) as EventSourceInput
        }
        // hiddenDays={[0]}
        slotMinTime={"08:00:00"}
        slotMaxTime={"18:00:00"}
        eventChange={async (arg) => {
          let event = arg.event;
          let appointment = data?.find((a) => a.id == event.id);
          if (!appointment) return;
          appointment.start = moment(event.start).format("YYYY-MM-DD HH:mm:ss");
          appointment.anchorDate = moment(event.start).format("YYYY-MM-DD");
          appointment.end = moment(event.end).format("YYYY-MM-DD HH:mm:ss");
          await apiFetch("appointments", "addOne", { appointment });
          setReload((prev) => prev + 1);
          onAppointmentChange(appointment);
        }}
        dateClick={async (arg) => {
          // if (!selectedUserId)
          //   return alert(
          //     `Veuillez choisir un agenda autre que "Rendez-vous non assignés"`
          //   );

          let start = moment(arg.date).format("YYYY-MM-DD HH:mm:ss");
          let end = moment(arg.date)
            .add(30, "minutes")
            .format("YYYY-MM-DD HH:mm:ss");

          if (onDateSelect)
            return await onDateSelect(
              start,
              end,
              selectedUserId || getUserId()
            );

          let appointment: Appointment = {
            start,
            end,
            anchorDate: moment(start).format("YYYY-MM-DD"),
            creator: getUserId(),
            doctor: (selectedUserId as any) || getItemId(USER.get()),
            service: getServiceId(),
            demandService: getServiceId(),
            status: "scheduled",
            type: selectedAppointmentTypes[0],
            patient: null,
            item: null,
          };

          if (patient) {
            appointment.patient = patient;
            return await openAppointmentFrom({
              appointment,
              patient,
              selectedUserId,
              onConfirm: (newValue) => {
                setReload((prev) => prev + 1);
              },
            });
          } else {
            await openDrawer({
              id: "patientSelectDrawer",
              title: "Choisir un patient",
              additionalClasses: ["small"],
              content: (
                <PatientsList
                  selectedPatient={appointment.patient}
                  onSelect={async (newPatient) => {
                    appointment.patient = newPatient;
                    return await openAppointmentFrom({
                      appointment,
                      patient: newPatient,
                      selectedUserId,
                      onConfirm: (newValue) => {
                        setReload((prev) => prev + 1);
                      },
                    });
                  }}
                ></PatientsList>
              ),
              footer: <div></div>,
            });
          }
        }}
        eventClick={async (arg) => {
          let appointment = data?.find((a) => a.id == arg.event.id);
          if (!appointment) return;
          console.log("appointment", appointment);
          await openAppointmentActionsDialog({
            appointment,
            appointments: data || [],
            index: 1,
            onChange: async (newAppointment) => {
              setReload((prev) => prev + 1);
            },
          });
        }}
        // eventDrop={(eventInfo) => {
        //     alert("drop");
        // }}

        // eventResize={(eventInfo) => {
        //     alert("resize");
        // }}
        // eventDragStop={(eventInfo) => {
        //     alert("stop");
        // }}

        eventContent={(arg) => {
          let appointment = data?.find((a) => a.id == arg.event.id);
          return (
            <div
              className="bold "
              style={{
                background: appointment?.type?.color || "blue",
                height: "100%",
                width: "100%",
                padding: "3px",
              }}
            >
              {getName(appointment?.patient) || "Sans nom"}
            </div>
          );
        }}
        dayHeaderContent={(arg) => {
          let date = moment(arg.date);
          return (
            <div className="dayHeader">
              <div className="c1-auto">
                <div>
                  <div>{date.format("ddd")}</div>
                  <div>
                    <strong>{date.format("DD")}</strong> {date.format("MMM")}
                  </div>
                  <div className="gray mtop5">
                    <span className="bold">
                      {
                        data?.filter(
                          (appointment) =>
                            moment(appointment.start).format("YYYY-MM-DD") ==
                            date.format("YYYY-MM-DD")
                        ).length
                      }
                    </span>{" "}
                    RDV(s)
                  </div>
                </div>
                <IconButton
                  onClick={() => {
                    printCalendarDay({
                      userId: selectedUserId,
                      date: date.format("YYYY-MM-DD"),
                      appointments: data || [],
                    });
                  }}
                >
                  <MdOutlinePrint size={20} />
                </IconButton>
              </div>
            </div>
          );
        }}
        slotLabelContent={(arg) => {
          return <div>{moment(arg.date).format("HH:mm")}</div>;
        }}
        nowIndicatorContent={(arg) => {
          return <div>nowindicator</div>;
        }}
        headerToolbar={{
          left: "",
          center: "",
          right: "",
        }}
        eventClassNames={(arg) => {
          return "event-class";
        }}
        titleFormat={{ year: "numeric", month: "long", day: "numeric" }}
        allDaySlot={false}
        displayEventEnd={true}
        dayMaxEvents={true}
        nowIndicator={false}
        scrollTimeReset={false}
        slotEventOverlap={false}
        editable={true}
        selectable={true}
        selectMirror={true}
      />
    </div>
  );
}
